import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const NOINDEX_PREFIXES = [
  '/auth',
  '/forgot-password',
  '/reset-password',
  '/profile',
  '/saved',
  '/notifications',
  '/add',
  '/edit',
  '/chat',
  '/complete-profile',
  '/admin',
  '/share-target',
];

const PUBLIC_META = {
  '/': {
    title: 'SchleiShare | Lebensmittel retten in der Schlei-Region',
    description: 'Überschüssige Lebensmittel kostenlos mit Nachbarn teilen, regionale Mittagstische entdecken und aktuelle Prospekte in Angeln und an der Schlei finden.',
    canonical: '/',
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://schleishare.de/#organization',
          name: 'SchleiShare',
          url: 'https://schleishare.de/',
          logo: 'https://schleishare.de/icon-maskable.svg',
          areaServed: ['Süderbrarup', 'Kappeln', 'Schleswig', 'Schlei-Region', 'Angeln'],
        },
        {
          '@type': 'WebSite',
          '@id': 'https://schleishare.de/#website',
          name: 'SchleiShare',
          url: 'https://schleishare.de/',
          inLanguage: 'de-DE',
          publisher: { '@id': 'https://schleishare.de/#organization' },
        },
        {
          '@type': 'WebApplication',
          name: 'SchleiShare',
          url: 'https://schleishare.de/',
          applicationCategory: 'LifestyleApplication',
          operatingSystem: 'Web, Android, iOS',
          isAccessibleForFree: true,
          description: 'Lokale Plattform zum Teilen und Retten von Lebensmitteln in der Schlei-Region.',
        },
      ],
    },
  },
  '/brochures': {
    title: 'Aktuelle Prospekte in der Schlei-Region | SchleiShare',
    description: 'Aktuelle Supermarkt-Prospekte und regionale Angebote für Süderbrarup, Kappeln und die Schlei-Region auf einen Blick.',
    canonical: '/brochures',
  },
  '/leaderboard': {
    title: 'Gemeinsam Lebensmittel retten | SchleiShare',
    description: 'Sieh, wie die SchleiShare-Community gemeinsam Lebensmittel rettet und regionales Teilen unterstützt.',
    canonical: '/leaderboard',
  },
  '/media': {
    title: 'SchleiShare weiterempfehlen | Grafiken und Vorlagen',
    description: 'Kostenlose Grafiken und Vorlagen, um SchleiShare in WhatsApp, Instagram und der Region bekannt zu machen.',
    canonical: '/media',
  },
  '/hygiene-richtlinien': {
    title: 'Hygiene-Richtlinien | SchleiShare',
    description: 'Wichtige Hinweise zum sicheren und verantwortungsvollen Teilen von Lebensmitteln über SchleiShare.',
    canonical: '/hygiene-richtlinien',
  },
  '/nutzungsbedingungen': {
    title: 'Nutzungsbedingungen | SchleiShare',
    description: 'Nutzungsbedingungen für die regionale Lebensmittel-Sharing-Plattform SchleiShare.',
    canonical: '/nutzungsbedingungen',
  },
  '/datenschutz': {
    title: 'Datenschutz | SchleiShare',
    description: 'Informationen zum Datenschutz und zur Verarbeitung personenbezogener Daten bei SchleiShare.',
    canonical: '/datenschutz',
  },
  '/impressum': {
    title: 'Impressum | SchleiShare',
    description: 'Anbieterkennzeichnung und Kontaktinformationen von SchleiShare.',
    canonical: '/impressum',
  },
};

const PRIVATE_META = {
  '/auth': ['Anmelden oder registrieren | SchleiShare', 'Melde dich bei SchleiShare an oder erstelle kostenlos ein Konto.'],
  '/forgot-password': ['Passwort vergessen | SchleiShare', 'Fordere einen sicheren Link zum Zurücksetzen deines SchleiShare-Passworts an.'],
  '/reset-password': ['Neues Passwort setzen | SchleiShare', 'Lege ein neues Passwort für dein SchleiShare-Konto fest.'],
  '/complete-profile': ['Profil vervollständigen | SchleiShare', 'Vervollständige dein persönliches SchleiShare-Profil.'],
};

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  return element;
};

const ensureCanonical = () => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  return element;
};

const getFeedMeta = (search) => {
  const tab = new URLSearchParams(search).get('tab');
  if (tab === 'commercial') {
    return {
      title: 'Mittagstisch und Wochenkarten in der Schlei-Region | SchleiShare',
      description: 'Aktuelle Mittagstische, Wochenkarten und Speisekarten regionaler Anbieter in Süderbrarup, Kappeln und Umgebung.',
      canonical: '/feed?tab=commercial',
    };
  }
  return {
    title: 'Lebensmittel kostenlos teilen und abholen | SchleiShare',
    description: 'Finde kostenlose Lebensmittelangebote von Privatpersonen in deiner Nähe oder teile überschüssiges Essen mit der SchleiShare-Community.',
    canonical: '/feed?tab=private',
  };
};

export const isNoindexRoute = (pathname) => NOINDEX_PREFIXES.some((prefix) => (
  pathname === prefix || pathname.startsWith(`${prefix}/`)
));

const RouteMetaPolicy = () => {
  const location = useLocation();

  useEffect(() => {
    const noindex = isNoindexRoute(location.pathname);
    ensureMeta('meta[name="robots"]', { name: 'robots' }).setAttribute(
      'content',
      noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large',
    );

    document.getElementById('schleishare-route-structured-data')?.remove();

    let meta = location.pathname === '/feed'
      ? getFeedMeta(location.search)
      : PUBLIC_META[location.pathname];

    if (noindex) {
      const privateEntry = PRIVATE_META[location.pathname];
      meta = {
        title: privateEntry?.[0] || 'Persönlicher Bereich | SchleiShare',
        description: privateEntry?.[1] || 'Geschützter persönlicher Bereich von SchleiShare.',
        canonical: location.pathname,
      };
    }

    if (!meta) return;

    const canonical = new URL(meta.canonical, window.location.origin).toString();
    document.title = meta.title;
    ensureCanonical().setAttribute('href', canonical);

    const tags = [
      ['meta[name="description"]', { name: 'description' }, meta.description],
      ['meta[property="og:title"]', { property: 'og:title' }, meta.title],
      ['meta[property="og:description"]', { property: 'og:description' }, meta.description],
      ['meta[property="og:url"]', { property: 'og:url' }, canonical],
      ['meta[name="twitter:title"]', { name: 'twitter:title' }, meta.title],
      ['meta[name="twitter:description"]', { name: 'twitter:description' }, meta.description],
      ['meta[name="twitter:url"]', { name: 'twitter:url' }, canonical],
    ];

    tags.forEach(([selector, attributes, content]) => {
      ensureMeta(selector, attributes).setAttribute('content', content);
    });

    if (meta.structuredData) {
      const script = document.createElement('script');
      script.id = 'schleishare-route-structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(meta.structuredData);
      document.head.appendChild(script);
    }
  }, [location.pathname, location.search]);

  return null;
};

export default RouteMetaPolicy;
